import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireRole } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, BookMarked, RefreshCw, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { uploadToBucket } from "@/lib/uploads";
import { extractCurriculumReference } from "@/lib/curriculum-reference-extract";
import {
  listCurriculumReferences,
  saveCurriculumReference,
  reindexCurriculumReference,
  deleteCurriculumReference,
  importCurriculumReferenceFromStorage,
} from "@/lib/curriculum-references.functions";

const BUNDLED = [
  {
    storagePath: "seed/ncdc-ecd-framework.txt",
    fileUrlPath: "seed/ncdc-ecd-framework.pdf",
    title: "Learning Framework for Early Childhood Development (3-6 years)",
    issuingBody: "Uganda NCDC",
    educationLevel: "Early Childhood",
    classRange: "Ages 3-6",
    description:
      "Official NCDC framework covering the five ECD learning outcomes: relating with others, exploring the environment, self-care, mathematical concepts and language development.",
  },
  {
    storagePath: "seed/p1-curriculum.txt",
    title: "National Primary School Curriculum for Uganda — Primary 1",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P1",
    description: "Thematic curriculum for Primary One: themes, competences, life skills and learning outcomes across all subjects.",
  },
  {
    storagePath: "seed/p2-teachers-guide.txt",
    title: "National Primary School Curriculum — Teacher's Guide, Primary 2",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P2",
    description: "Teacher's guide for the P2 thematic curriculum: themes, sub-themes, competences and suggested teaching activities.",
  },
  {
    storagePath: "seed/p2-english-trb.txt",
    title: "English Teacher's Resource Book — Primary 2",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P2",
    subject: "English",
    description: "NCDC English resource book for Primary Two: oral work, reading, writing and language structures.",
  },
  {
    storagePath: "seed/p3-teachers-guide.txt",
    title: "National Primary School Curriculum — Teacher's Guide, Primary 3",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P3",
    description: "Teacher's guide for Primary Three covering all thematic learning areas and the transition to English as medium of instruction.",
  },
  {
    storagePath: "seed/p4-mathematics.txt",
    title: "Mathematics Syllabus — Primary Four",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P4",
    subject: "Mathematics",
    description: "Official 2010 NCDC Mathematics teaching syllabus for P4: topics, competences, content, methods and assessment.",
  },
  {
    storagePath: "seed/p4-integrated-science.txt",
    title: "Integrated Science Syllabus — Primary Four",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P4",
    subject: "Integrated Science",
    description: "Official 2010 NCDC Integrated Science teaching syllabus for P4: themes, competences, content and practical activities.",
  },
  {
    storagePath: "seed/p5-creative-arts-pe.txt",
    title: "Primary Five Teaching Syllabus — Creative Arts and Physical Education",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P5",
    subject: "Creative Arts & PE",
    description: "NCDC P5 syllabus Set Two: creative arts and physical education competences, content and assessment guidance.",
  },
  {
    storagePath: "seed/p6-core-subjects.txt",
    title: "Primary Six Curriculum — Set One (Core Subjects)",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P6",
    description: "NCDC 2010 P6 syllabus for English, Integrated Science, Local Language, Mathematics, Religious Education (CRE & IRE) and Social Studies.",
  },
  {
    storagePath: "seed/p7-cape.txt",
    title: "Primary Seven Curriculum — Set Two (CAPE: MDD, PE, Art & Technology)",
    issuingBody: "Uganda NCDC",
    educationLevel: "Primary",
    classRange: "P7",
    subject: "Creative Arts & PE",
    description: "NCDC 2012 P7 syllabus for Music, Dance & Drama, Physical Education and Art & Technology, with PLE assessment guidance.",
  },
];



export const Route = createFileRoute("/admin/curriculum-references")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({
    meta: [
      { title: "Curriculum References · MAKAI" },
      { name: "description", content: "Upload NCDC syllabus and curriculum guides so MAKAI grounds every lesson, question and answer in the official Uganda curriculum." },
      { property: "og:title", content: "Curriculum References · MAKAI" },
      { property: "og:description", content: "Official Uganda NCDC syllabus documents indexed into the MAKAI AI knowledge base." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

function Page() {
  const list = useServerFn(listCurriculumReferences);
  const save = useServerFn(saveCurriculumReference);
  const reindex = useServerFn(reindexCurriculumReference);
  const remove = useServerFn(deleteCurriculumReference);
  const importFromStorage = useServerFn(importCurriculumReferenceFromStorage);

  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [issuingBody, setIssuingBody] = useState("Uganda NCDC");
  const [educationLevel, setEducationLevel] = useState("");
  const [classRange, setClassRange] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const refs = useQuery({ queryKey: ["curriculum-references"], queryFn: () => list() });

  const onPick = (f: File | null) => {
    setFile(f);
    if (f && !title) setTitle(f.name.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " "));
  };

  const onUpload = async () => {
    if (!file) return toast.error("Choose a syllabus file first.");
    if (title.trim().length < 3) return toast.error("Give the document a title.");
    setBusy(true);
    const t = toast.loading("Reading the document…");
    try {
      const { text, pages } = await extractCurriculumReference(file);
      toast.loading("Uploading and indexing…", { id: t });
      let storagePath: string | null = null;
      let sourceUrl: string | null = null;
      try {
        const up = await uploadToBucket("curriculum-references", file);
        storagePath = up.path;
        sourceUrl = up.url || null;
      } catch {
        /* text is what matters — keep going if the file upload fails */
      }
      const res = await save({
        data: {
          title: title.trim(),
          issuingBody: issuingBody.trim() || "Uganda NCDC",
          curriculum: issuingBody.trim() || "Uganda NCDC",
          educationLevel: educationLevel.trim() || null,
          classRange: classRange.trim() || null,
          subject: subject.trim() || null,
          description: description.trim() || null,
          sourceUrl,
          storagePath,
          pageCount: pages || null,
          contentText: text,
        },
      });
      toast.success(`Indexed ${res.embedded}/${res.chunks} chunks into the AI knowledge base`, { id: t });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setTitle(""); setEducationLevel(""); setClassRange(""); setSubject(""); setDescription("");
      refs.refetch();
    } catch (e) {
      toast.error((e as Error).message ?? "Upload failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const onReindex = async (id: string) => {
    const t = toast.loading("Re-indexing…");
    try {
      const r = await reindex({ data: { id } });
      toast.success(`Re-indexed ${r.embedded}/${r.chunks} chunks`, { id: t });
      refs.refetch();
    } catch (e) {
      toast.error((e as Error).message ?? "Re-index failed", { id: t });
    }
  };

  const onImport = async (b: (typeof BUNDLED)[number]) => {
    const t = toast.loading("Importing and indexing…");
    try {
      const r = await importFromStorage({ data: b });
      toast.success(`Indexed ${r.embedded}/${r.chunks} chunks`, { id: t });
      refs.refetch();
    } catch (e) {
      toast.error((e as Error).message ?? "Import failed", { id: t });
    }
  };

  const onDelete = async (id: string) => {
    const t = toast.loading("Removing…");
    try {
      await remove({ data: { id } });
      toast.success("Reference removed", { id: t });
      refs.refetch();
    } catch (e) {
      toast.error((e as Error).message ?? "Delete failed", { id: t });
    }
  };

  return (
    <AdminShell
      title="Curriculum References"
      subtitle="Official NCDC syllabus guides that ground every MAKAI lesson, question and answer"
    >
      <div className="mx-auto max-w-4xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> Add a syllabus document</CardTitle>
            <CardDescription>
              PDF, DOCX, TXT or MD. MAKAI extracts the text, embeds it into the AI knowledge base and cites it when
              planning lessons, generating questions or tutoring learners.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cr-file">Document</Label>
              <Input
                id="cr-file"
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.txt,.md"
                onChange={(e) => onPick(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cr-title">Title</Label>
                <Input id="cr-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Learning Framework for Early Childhood Development" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cr-body">Issuing body</Label>
                <Input id="cr-body" value={issuingBody} onChange={(e) => setIssuingBody(e.target.value)} placeholder="Uganda NCDC" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cr-level">Education level</Label>
                <Input id="cr-level" value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)} placeholder="Early Childhood / Primary / O-Level" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cr-class">Class range</Label>
                <Input id="cr-class" value={classRange} onChange={(e) => setClassRange(e.target.value)} placeholder="Ages 3-6 / P1-P7 / S1-S4" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cr-subject">Subject (optional)</Label>
                <Input id="cr-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Mathematics" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cr-desc">Notes (optional)</Label>
              <Textarea id="cr-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What this guide covers and when teachers should use it." />
            </div>
            <Button onClick={onUpload} disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {busy ? "Indexing…" : "Upload & index"}
            </Button>
          </CardContent>
        </Card>

        {BUNDLED.some((b) => !refs.data?.some((r) => r.title === b.title)) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BookMarked className="h-5 w-5 text-primary" /> Ready to import</CardTitle>
              <CardDescription>Official NCDC guides already prepared for this platform.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {BUNDLED.filter((b) => !refs.data?.some((r) => r.title === b.title)).map((b) => (
                <div key={b.storagePath} className="flex flex-wrap items-start gap-3 rounded-lg border p-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{b.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{b.description}</p>
                  </div>
                  <Button size="sm" className="gap-1.5" onClick={() => onImport(b)}>
                    <Upload className="h-3.5 w-3.5" /> Import & index
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><BookMarked className="h-5 w-5" /> Indexed references</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {refs.isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : !refs.data?.length ? (
              <p className="text-sm text-muted-foreground">No curriculum references yet. Upload the first NCDC guide above.</p>
            ) : (
              refs.data.map((r) => (
                <div key={r.id} className="flex flex-wrap items-start gap-3 rounded-lg border p-3">
                  <FileText className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{r.title}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge variant="secondary" className="text-xs">{r.issuing_body}</Badge>
                      {r.education_level && <Badge variant="outline" className="text-xs">{r.education_level}</Badge>}
                      {r.class_range && <Badge variant="outline" className="text-xs">{r.class_range}</Badge>}
                      {r.subject && <Badge variant="outline" className="text-xs">{r.subject}</Badge>}
                      <Badge variant="outline" className="text-xs">{r.chunk_count} chunks</Badge>
                      {r.page_count ? <Badge variant="outline" className="text-xs">{r.page_count} pages</Badge> : null}
                    </div>
                    {r.description && <p className="mt-2 text-sm text-muted-foreground">{r.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onReindex(r.id)}>
                      <RefreshCw className="h-3.5 w-3.5" /> Re-index
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(r.id)} aria-label={`Delete ${r.title}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
